// AUTO-GENERATED — do not edit. Source: /shared/. Run scripts/sync-shared.sh to update.
/**
 * SLIK — Professional Practice Index (PPI) Brain (2026-08-05)
 * ==========================================================
 * ONE source of truth for how a barber is measured AS A PROFESSIONAL. Pure logic — NO
 * React, NO Firebase. Every surface (POS profile, coaching engine, compensation, emergency
 * privilege gate) reads from here so "how good is this barber / are they trusted with X"
 * can never drift between screens.
 *
 * Jose's framework (The Professional Practice Initiative): we don't measure people to
 * reward or punish — we measure to REVEAL WHERE THEY NEED COACHING. Every metric has three
 * outcomes: Recognition, Coaching, Growth. Never punishment.
 *
 * BPI (Barber Performance Indicator) is ONE instrument inside PPI. The emergency-booking
 * privilege reads a SPECIFIC subset of pillars (see EMERGENCY_PRIVILEGE_REQUIREMENTS), NOT
 * a single revenue number — emergency bookings represent the shop, so they're gated on TRUST.
 *
 * DO NOT EDIT the per-app copies in <app>/src/shared/ — edit THIS file at repo root and run
 * scripts/sync-shared.sh to propagate.
 *
 * All scores are 0-100. Coaches may enter Technical sub-measures 0-10; scale ×10 on entry.
 *
 * ── THREE LAYERS (Jose, 2026-08-05) ─────────────────────────────────────────────────
 *   Layer 1 — PHILOSOPHY:  The Professional Practice Initiative — "what kind of professional
 *                          are we developing?" Never changes.
 *   Layer 2 — FRAMEWORK:   This file. The PPI — "how do we measure professional growth?"
 *   Layer 3 — COACHING:    The AI PRACTICE COACH (not "manager" — coaches develop, managers
 *                          evaluate) — "how do we help someone improve?" Reads this brain,
 *                          then explains WHY, WHAT it means, HOW to improve, and when to review.
 *   The better question the PPI asks isn't "how is this employee doing?" but
 *   "WHO IS THIS PROFESSIONAL BECOMING?" — and one day the same language bridges partner
 *   schools → SLIK, so graduates continue the same development, not start over.
 *
 * ── TWO FOUNDATIONAL RULES ──────────────────────────────────────────────────────────
 *   1. ONLY MEASURE WHAT THE BARBER CAN INFLUENCE. External variables (no-shows, weather,
 *      shop marketing, walk-in traffic, economy, random cancellations) are NEVER direct
 *      performance measures. (Retention IS fair — relationships reduce churn.)
 *   2. SEPARATE PROCESS FROM OUTCOME (pillar.kind). Process = behaviors (technique, greeting,
 *      cleanliness…); Outcome = results (retail, ticket, retention…). The coach uses this so a
 *      low number gets the right response: strong process + weak outcome → coach STRATEGY;
 *      weak process + strong outcome → coach SUSTAINABILITY. See coachingLens().
 */

// ── The seven pillars + weights (weights sum to 100) ────────────────────────────────
export interface PpiPillar {
  key: PillarKey;
  label: string;
  weight: number;        // % of the composite PPI
  kind: 'process' | 'outcome';   // Rule 2: behaviors vs results — drives the coaching lens
  blurb: string;
  measures: string[];    // what a coach observes to score this pillar
}

export type PillarKey =
  | 'technical' | 'communication' | 'clientExperience' | 'business'
  | 'professionalHabits' | 'growth' | 'leadership';

export const PPI_PILLARS: PpiPillar[] = [
  {
    key: 'technical', label: 'Technical Excellence', weight: 25,
    kind: 'process',
    blurb: 'Can the barber actually perform?',
    measures: ['Fade quality', 'Shear work', 'Long-hair competency', 'Beard work',
      'Consultation accuracy', 'Consistency', 'Speed without sacrificing quality', 'Finish quality'],
  },
  {
    key: 'clientExperience', label: 'Client Experience', weight: 20,
    kind: 'process',
    blurb: 'The chair, the room, the feeling.',
    measures: ['Punctuality', 'Chair cleanliness', 'Tool organization', 'Professional appearance',
      'Comfort', 'Hospitality', 'Music awareness', 'Environment', 'Overall experience'],
  },
  {
    key: 'professionalHabits', label: 'Professional Habits', weight: 15,
    kind: 'process',
    blurb: 'Reliability and ownership.',
    measures: ['Attendance', 'Reliability', 'On time', 'Dress code', 'Phone usage', 'Teamwork',
      'Initiative', 'Ownership', 'Responsibility', 'Problem solving'],
  },
  {
    key: 'communication', label: 'Professional Communication', weight: 15,
    kind: 'process',
    blurb: 'Greeting to thank-you — the whole conversation.',
    measures: ['Greeting', 'Eye contact', 'Smile', 'Consultation', 'Checking in during haircut',
      'Explaining recommendations', 'Professional confidence', 'Closing conversation',
      'Thanking client', 'Client education', 'Not over-talking', 'Listening'],
  },
  {
    key: 'business', label: 'Business Performance', weight: 10,
    kind: 'outcome',
    blurb: 'Value created — ONE pillar, never the whole story.',
    // NOTE: no-show percentage is deliberately EXCLUDED — it's outside the barber's control.
    measures: ['Retail', 'Rebooking', 'Upsells', 'Client retention', 'Referral generation',
      'Average ticket', 'Emergency booking acceptance', 'Utilization rate',
      'Cancellation management'],
  },
  {
    key: 'growth', label: 'Growth Mindset', weight: 10,
    kind: 'process',
    blurb: 'Do they keep getting better?',
    measures: ['Attends education', 'Practices', 'Requests coaching', 'Accepts feedback',
      'Implements feedback', 'Learns new services', 'Expands haircut library', 'Teaches others',
      'Self reflection'],
  },
  {
    key: 'leadership', label: 'Leadership', weight: 5,
    kind: 'process',
    blurb: 'Eventually everyone should grow here.',
    measures: ['Mentors juniors', 'Helps teammates', 'Protects culture', 'Positive attitude',
      'Conflict resolution', 'Professional influence', 'Represents SLIK'],
  },
];

// ── Development levels (healthier language than Poor/Average/Excellent) ──────────────
export interface PpiLevel {
  key: 'foundation' | 'developing' | 'proficient' | 'advanced' | 'master';
  label: string;
  subtitle: string;
  min: number;           // inclusive lower bound (0-100)
}

// Bands are tunable — kept here so every surface agrees on what "Proficient" means.
export const PPI_LEVELS: PpiLevel[] = [
  { key: 'master',     label: 'Master',       subtitle: 'Raises the standard', min: 90 },
  { key: 'advanced',   label: 'Advanced',     subtitle: 'Leads others',        min: 75 },
  { key: 'proficient', label: 'Proficient',   subtitle: 'Consistent',          min: 60 },
  { key: 'developing', label: 'Developing',   subtitle: 'Improving',           min: 40 },
  { key: 'foundation', label: 'Foundation',   subtitle: 'Needs coaching',      min: 0 },
];

export function levelForScore(score: number): PpiLevel {
  for (const l of PPI_LEVELS) if (score >= l.min) return l;
  return PPI_LEVELS[PPI_LEVELS.length - 1];
}

// ── The per-barber scorecard ────────────────────────────────────────────────────────
/** Coach-entered (and later partly auto-derived) 0-100 scores. Missing pillar = 0 (unassessed). */
export interface PpiScores {
  technical?: number;
  communication?: number;
  clientExperience?: number;
  business?: number;
  professionalHabits?: number;
  growth?: number;
  leadership?: number;
  // Gate-specific metrics tracked distinctly (also live inside their pillars):
  attendance?: number;   // 0-100 (part of Professional Habits, but the emergency gate needs it alone)
  retention?: number;    // 0-100 client retention (part of Business; gate compares to shop average)
}

export interface PpiResult {
  ppi: number;                                   // 0-100 composite
  level: PpiLevel;
  pillars: Array<{ key: PillarKey; label: string; weight: number; score: number; level: PpiLevel }>;
  assessed: boolean;                             // any pillar scored yet?
}

/** Composite PPI = weighted average of pillars that have a score. Unassessed pillars are skipped. */
export function computePPI(scores: PpiScores | null | undefined): PpiResult {
  const s = scores || {};
  const pillars: PpiResult['pillars'] = [];
  let wSum = 0, acc = 0, assessed = false;
  for (const p of PPI_PILLARS) {
    const raw = (s as any)[p.key];
    const has = typeof raw === 'number' && raw > 0;
    const score = has ? Math.max(0, Math.min(100, raw)) : 0;
    if (has) { acc += score * p.weight; wSum += p.weight; assessed = true; }
    pillars.push({ key: p.key, label: p.label, weight: p.weight, score, level: levelForScore(score) });
  }
  const ppi = wSum > 0 ? Math.round(acc / wSum) : 0;
  return { ppi, level: levelForScore(ppi), pillars, assessed };
}

// ── Emergency-booking privilege gate — trust, not revenue ───────────────────────────
/**
 * Jose's exact requirements. Emergency bookings represent the shop → not everyone qualifies.
 * Retention is compared to the SHOP AVERAGE (passed in). Each requirement is surfaced so the
 * profile can show a met/not-met checklist that doubles as a coaching target list.
 */
export interface PrivilegeRequirement {
  key: string;
  label: string;
  need: string;                                  // human-readable target
  have: number;                                  // barber's current value
  met: boolean;
}

export const PROFICIENT_MIN = 60;   // "Proficient" level threshold (mirrors PPI_LEVELS)

export interface EmergencyPrivilege {
  eligible: boolean;                             // all requirements met
  requirements: PrivilegeRequirement[];
  unmet: PrivilegeRequirement[];
}

export function emergencyPrivilegeCheck(
  scores: PpiScores | null | undefined,
  shopAvgRetention: number = 50,
): EmergencyPrivilege {
  const s = scores || {};
  const val = (k: keyof PpiScores) => (typeof s[k] === 'number' ? (s[k] as number) : 0);
  const requirements: PrivilegeRequirement[] = [
    { key: 'technical',    label: 'Technical',       need: `Proficient (${PROFICIENT_MIN}+)`, have: val('technical'),          met: val('technical') >= PROFICIENT_MIN },
    { key: 'habits',       label: 'Professional Habits', need: '90+',                          have: val('professionalHabits'), met: val('professionalHabits') >= 90 },
    { key: 'attendance',   label: 'Attendance',      need: '95+',                              have: val('attendance'),         met: val('attendance') >= 95 },
    { key: 'communication',label: 'Communication',   need: '85+',                              have: val('communication'),      met: val('communication') >= 85 },
    { key: 'retention',    label: 'Client retention', need: `≥ shop avg (${Math.round(shopAvgRetention)})`, have: val('retention'), met: val('retention') >= shopAvgRetention },
  ];
  const unmet = requirements.filter(r => !r.met);
  return { eligible: unmet.length === 0, requirements, unmet };
}

/**
 * Coaching-engine seed: the top growth areas = the barber's lowest-scoring pillars. The real
 * coaching-plan generator (goals, mannequin reps, retest) lives in the app layer and builds on
 * this — see the coach subsystem. Returned lowest-first.
 */
export function topGrowthAreas(scores: PpiScores | null | undefined, n: number = 3): Array<{ key: PillarKey; label: string; score: number }> {
  return computePPI(scores).pillars
    .filter(p => p.score > 0)
    .sort((a, b) => a.score - b.score)
    .slice(0, n)
    .map(p => ({ key: p.key, label: p.label, score: p.score }));
}

/**
 * Coaching lens (Layer-3 seed, Rule 2): compares PROCESS behaviors vs OUTCOME results so the
 * AI Practice Coach never assumes every low number has the same cause.
 *   strong process + weak outcome → coach STRATEGY (behaviors are there; convert them to results)
 *   weak process + strong outcome → coach SUSTAINABILITY (results are luck/volume; build the habits)
 * `strong` defaults to Proficient (60).
 */
export function coachingLens(
  scores: PpiScores | null | undefined,
  strong: number = PROFICIENT_MIN,
): { processAvg: number; outcomeAvg: number; lens: 'strategy' | 'sustainability' | 'on-track' | 'develop'; note: string } {
  const res = computePPI(scores);
  const proc = res.pillars.filter(p => PPI_PILLARS.find(x => x.key === p.key)?.kind === 'process' && p.score > 0);
  const out = res.pillars.filter(p => PPI_PILLARS.find(x => x.key === p.key)?.kind === 'outcome' && p.score > 0);
  const avg = (arr: typeof proc) => (arr.length ? Math.round(arr.reduce((a, b) => a + b.score, 0) / arr.length) : 0);
  const processAvg = avg(proc), outcomeAvg = avg(out);
  let lens: 'strategy' | 'sustainability' | 'on-track' | 'develop', note: string;
  if (processAvg >= strong && outcomeAvg < strong) {
    lens = 'strategy'; note = 'Behaviors are strong but results lag — coach strategy (turn good practice into rebooking, retail, retention).';
  } else if (processAvg < strong && outcomeAvg >= strong) {
    lens = 'sustainability'; note = 'Results are good but the behaviors underneath are weak — coach sustainability (build the habits so it lasts).';
  } else if (processAvg >= strong && outcomeAvg >= strong) {
    lens = 'on-track'; note = 'Process and outcomes both strong — recognize, then stretch toward leadership.';
  } else {
    lens = 'develop'; note = 'Early in the journey — focus on the foundational process pillars first.';
  }
  return { processAvg, outcomeAvg, lens, note };
}
